import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import * as moment from "moment";
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTimesCircle, faCheckCircle, faUndo, faPen, faDollarSign, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { MatDialog } from "@angular/material";
import { Subject } from "rxjs";
import { Order } from "src/app/core/models/order/order.model";
import { CrudService } from "src/app/core/services/data/crud.service";
import { ErrorHandlerService } from "src/app/core/services/util/error-handler.service";
import { LoaderMessagingService } from "src/app/core/services/messaging/loader-messaging.service";
import { ConfirmDialogComponent } from "src/app/shared/components/confirm-dialog/confirm-dialog.component";
import { DialogBoxComponent } from "src/app/shared/components/dialog-box/dialog-box.component";
import { dialogBoxSuccessTitle } from "src/app/core/constants/message.constant";
import {
  PROCESSING_STATUS_1,
  PROCESSING_STATUS_2,
  PROCESSING_STATUS_3,
  PROCESSING_STATUS_4,
  PROCESSING_STATUS_5,
  ORDER_ITEM_TYPE_3,
  PROCESSING_STATUS_6,
  PROCESSING_STATUS_7,
  ORDER_ITEM_TYPE_2,
} from "src/app/core/constants/configuration/order-constants.config";
import { UserService } from "src/app/core/services/util/user.service";
import { ADMIN, SUPERVISOR, SPECIALIST } from 'src/app/core/constants/configuration/role-constants.config';
import { OrderItemAdjustmentDialogComponent } from '../order-item-adjustment-dialog/order-item-adjustment-dialog.component';
import { OrderItemStatusHistory } from '../../../../core/models/order/order-item-status-history.model';
import { getItemLatestStatusHistoryEndpoint, getItemAdjustmentHistoryEndpoint, getItemCreatedStatusHistoryEndpoint } from '../../../../core/constants/endpoints.constant';
import { OrderItemAdjustmentHistory } from '../../../../core/models/order/order-item-adjustment-history.model';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-order-item-books',
  templateUrl: './order-item-books.component.html',
  styleUrls: ['./order-item-books.component.scss']
})
export class OrderItemBooksComponent implements OnInit {

  @Input() canRemoveItem: boolean = true;
  @Input() type: String;
  @Input() orderItemAssoc: any[] = [];
  @Input() processingStatus: any[];
  @Output() onGetNewOrderDetails = new EventEmitter<any>();
  @Output() onCancel = new EventEmitter<any>();
  @Output() onPreComplete = new EventEmitter<any>();
  @Output() onComplete = new EventEmitter<any>();
  @Output() onAdjust = new EventEmitter<any>();
  @Output() requestToCancel = new EventEmitter<any>();
  @Output() undoRequestToCancel = new EventEmitter<any>();
  orderDetailsValue: Order;
  orderItemsList: any[] = [];
  orderGroupCodes: string[] = [];
  unsubscribe: Subject<any> = new Subject();
  referenceTypes: any[];
  selectedItems: any[] = [];
  selectedItemStatusHistory: OrderItemStatusHistory;
  selectedItemAdjustmentHistory: OrderItemAdjustmentHistory;
  
  canRequestToCancel: boolean = false;
  canUndoRequestToCancel: boolean = false;

  completeIcon: IconDefinition = faCheckCircle;
  removeIcon: IconDefinition = faTimesCircle;
  undoIcon: IconDefinition = faUndo;
  editIcon: IconDefinition = faPen;
  refundIcon: IconDefinition = faDollarSign;

  REQUEST_CANCEL: any = { action: "REQUEST_CANCEL", label: ""  };
  UNDO_REQUEST_CANCEL: any = { action: "UNDO_REQUEST_CANCEL", label: ""  };
  PRE_COMPLETE: any = { action: "PRE_COMPLETE", label: "pre-completed"  };
  COMPLETE: any = { action: "COMPLETE", label: "completed"  };
  CANCEL: any = { action: "CANCEL", label: "cancelled"  };

  collapse: boolean = false;
  //ICONS
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;

  collapsed() {
    this.collapse = !this.collapse;
  }

  constructor(
    private crudService: CrudService,
    private dialog: MatDialog,
    private errorHandlerService: ErrorHandlerService,
		private loaderMessagingService: LoaderMessagingService,
		private userService: UserService,
  ) {
    this.loaderMessagingService.showPageLoader(true);
  }

  ngOnInit() {
    this.loaderMessagingService.showPageLoader(false);
  }

  @Input()
  set orderDetails(value) {
    this.orderDetailsValue = value;
    this.selectedItems = [];
    this.groupItemsByCurriculum();
  }

  groupItemsByCurriculum() {
    this.orderItemsList = this.orderDetailsValue.orderItems.filter(item => item.referenceType == this.type);
    this.orderItemsList.forEach(item => {
      this.getItemCreatedStatusDetails(item)
    });
    if (this.orderItemAssoc.length > 0) {
      let tempList = [];
      this.orderItemAssoc.forEach(itemAssoc => {
        this.orderItemsList.forEach(item => {
          if (item.id === itemAssoc.txnOrderItemId) tempList.push(item);
        });
      });
      this.orderItemsList = tempList;
    }

    this.orderDetailsValue.orderItems.forEach(item => {
      if (!this.orderGroupCodes.includes(item.txnOrderGroup)) this.orderGroupCodes.push(item.txnOrderGroup);
    });
    this.orderGroupCodes.sort();
  }

  formatAmount(value: number): string {
    return "$" + value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
  }

  formatDate(value: Date): string {
    return moment(value).format('MMM DD, YYYY')
  }
  
  onRemoveItem(item) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { autoFocus: false });
    dialogRef.componentInstance.confirmMessage = "Are you sure you want to remove this book?";
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.onGetNewOrderDetails.emit(item);
        const dialogRef = this.dialog.open(DialogBoxComponent);
        dialogRef.componentInstance.dialogTitle = dialogBoxSuccessTitle;
        dialogRef.componentInstance.contentMessage = 'Book was successfully removed.';
      }
    });
  }

  get isUserTeacher(): boolean {
    return !this.userService.hasRole([ADMIN, SUPERVISOR, SPECIALIST]);
  }

  hasOtherBooks(code: string): boolean {
    return this.orderItemsList.filter(item => item.txnOrderGroup === code &&
      item.referenceType === ORDER_ITEM_TYPE_3).length > 0;
  }

  isItemSelected(item: any): boolean {
    return this.selectedItems.filter(selectedItem => selectedItem.referenceCode === item.referenceCode && selectedItem.id === item.id).length > 0
  }

  isAllItemSelected() {
    return this.selectedItems.length > 0 && this.selectedItems.length === this.orderItemsList.length
  }

  isRefunded(item) {
    return item.status === PROCESSING_STATUS_6
  }

  isAdjusted(item) {
    return item.status === PROCESSING_STATUS_7
  }

  onSelectAll($event: any) {
    if ($event.checked) {
      this.selectedItems = this.orderItemsList;
    } else {
      this.selectedItems = [];
    }
    if (this.selectedItems.length === 1 && this.selectedItems[0].forCancel &&
        [PROCESSING_STATUS_3, PROCESSING_STATUS_4, PROCESSING_STATUS_6, PROCESSING_STATUS_7].includes(this.selectedItems[0].status)) 
      this.getItemLatestStatusHistory(this.selectedItems[0].id);
      this.canRequestToCancel = this.type != ORDER_ITEM_TYPE_2 && this.selectedItems.length > 0 &&
        this.selectedItems.filter(selectedItem => selectedItem.forCancel || selectedItem.status === PROCESSING_STATUS_1 || selectedItem.status === PROCESSING_STATUS_5 ||
          selectedItem.status === PROCESSING_STATUS_6 || selectedItem.status === PROCESSING_STATUS_7 || selectedItem.withdrawn).length === 0;
      this.canUndoRequestToCancel = this.type != ORDER_ITEM_TYPE_2 && this.selectedItems.length > 0 && this.selectedItems.filter(selectedItem =>
      !selectedItem.forCancel || (selectedItem.resourceOrderDetails && selectedItem.resourceOrderDetails.required)).length === 0;
  }

  onSelectItem($event: any, item: any) {
    if ($event.checked) {
      this.selectedItems.push(item);
    } else {
      this.selectedItems = this.selectedItems.filter(selectedItem => selectedItem.id !== item.id);
    }
    if (this.selectedItems.length === 1 && this.selectedItems[0].forCancel &&
        [PROCESSING_STATUS_3, PROCESSING_STATUS_4, PROCESSING_STATUS_6, PROCESSING_STATUS_7].includes(this.selectedItems[0].status)) 
      this.getItemLatestStatusHistory(this.selectedItems[0].id);
      this.canRequestToCancel = this.type != ORDER_ITEM_TYPE_2 && this.selectedItems.length > 0 &&
        this.selectedItems.filter(selectedItem => selectedItem.forCancel || selectedItem.status === PROCESSING_STATUS_1 || selectedItem.status === PROCESSING_STATUS_5 ||
          selectedItem.status === PROCESSING_STATUS_6 || selectedItem.status === PROCESSING_STATUS_7 || selectedItem.withdrawn).length === 0;
      this.canUndoRequestToCancel = this.type != ORDER_ITEM_TYPE_2 && this.selectedItems.length > 0 && this.selectedItems.filter(selectedItem =>
        !selectedItem.forCancel || (selectedItem.resourceOrderDetails && selectedItem.resourceOrderDetails.required)).length === 0;
  }

  getStatusName(statusCode: string) {
    const statusName: any = this.processingStatus.find(status => status.code === statusCode);
    return statusName ? statusName.name : "";
  }

  getStatusClass(statusCode: string): string {
    switch (statusCode) {
      case PROCESSING_STATUS_1: return "approved status"
      case PROCESSING_STATUS_2: return "processing status"
      case PROCESSING_STATUS_3: return "pre-completed status"
      case PROCESSING_STATUS_4: return "completed status"
      case PROCESSING_STATUS_5: return "canceled status"
      case PROCESSING_STATUS_6: return "refunded status"
      case PROCESSING_STATUS_7: return "adjusted status"
      default: return "status"
    }
  }

  getResourceTypeName(type: string) {
    return "Workbook/Text Book"
  }

  hasAdditionalItemDetails(item) {
    return (item.forCancel || this.isShowModifiedDate(item))
  }

  isShowModifiedDate(item) {
    return (item.id != 0 && item.id != null) && [PROCESSING_STATUS_3, PROCESSING_STATUS_4].includes(item.status)
  }

  get canPreComplete(): boolean {
    return this.selectedItems.length > 0 && 
      this.selectedItems.filter(item => item.status !== PROCESSING_STATUS_2 || item.forCancel).length === 0
  }

  get canComplete(): boolean {
    return this.selectedItems.length > 0 && 
      this.selectedItems.filter(item => ![PROCESSING_STATUS_2, PROCESSING_STATUS_3].includes(item.status) || item.forCancel).length === 0
  }

  get canAdjust(): boolean {
    return this.selectedItems.length === 1 &&
      [PROCESSING_STATUS_3, PROCESSING_STATUS_4, PROCESSING_STATUS_6, PROCESSING_STATUS_7].includes(this.selectedItems[0].status);
  }

  get canCancel(): boolean {
    return this.type != ORDER_ITEM_TYPE_2 && this.selectedItems.length == 1 && (this.isUserTeacher ? this.selectedItems.length > 0 &&
      this.selectedItems.filter(item => !(item.status === PROCESSING_STATUS_1)).length === 0 && this.selectedItems.filter(item => item.inBundle).length <= 1
      : this.selectedItems.length > 0 && this.selectedItems.filter(item => item.inBundle).length <=1 && !this.isPastThirtyDays && this.orderDetailsValue.syCode != "2019-2020");;
  }

  get canNewCancel(): boolean {
    if(this.selectedItems.length <= 0 || this.selectedItems.length > 1 || this.type == ORDER_ITEM_TYPE_2 ) {
      return true;
    } else {
      if(this.isUserTeacher) {
        return this.selectedItems.length === 1 &&
          this.selectedItems.filter(item => !(item.status === PROCESSING_STATUS_2 || item.status === PROCESSING_STATUS_3 ||
          item.status === PROCESSING_STATUS_4 || item.status === PROCESSING_STATUS_5 ||
          item.status === PROCESSING_STATUS_6 || item.status === PROCESSING_STATUS_7)).length === 0 &&
          this.selectedItems.filter(item => item.inBundle).length <= 1 && this.orderDetailsValue.syCode != "2019-2020";
      } else {
        return this.selectedItems.length === 1 &&
          this.selectedItems.filter(item => !(item.status === PROCESSING_STATUS_3 ||
          item.status === PROCESSING_STATUS_4 || item.status === PROCESSING_STATUS_5 ||
          item.status === PROCESSING_STATUS_6 || item.status === PROCESSING_STATUS_7)).length === 0 &&
          this.selectedItems.filter(item => item.inBundle).length <= 1 && this.orderDetailsValue.syCode != "2019-2020";
      }
    }
  }

  get canRefund(): boolean {
    return (this.selectedItems.length === 1 && this.selectedItems[0].forCancel &&
      [PROCESSING_STATUS_3, PROCESSING_STATUS_4, PROCESSING_STATUS_6].includes(this.selectedItems[0].status) &&
      this.isPastThirtyDays && (this.selectedItems[0].newPrice === null || this.selectedItems[0].newDeposit === null || 
      this.selectedItems[0].newPerClassCost === null) && this.selectedItems[0].adjustedPrice === null && 
      this.selectedItems[0].adjustedDeposit === null && this.selectedItems[0].adjustedPerClassCost === null) && this.orderDetailsValue.syCode != "2019-2020";;
  }

  get isPastThirtyDays(): boolean {
    if (this.selectedItems.length === 1 && this.selectedItemStatusHistory) {
      let dateLimit = moment(this.selectedItemStatusHistory.dateModified).add(30, 'days');
      return moment(this.selectedItems[0].rtcModifiedDate).isAfter(dateLimit);
    } else return false;
  }

  showConfirmDialog(code: any) {
    let selectedItemsCodes: string[] = [];
    this.selectedItems.slice().forEach(item => {
      selectedItemsCodes.push(item.referenceCode)
    });
    if (!this.selectedItems[0].assetCode && !this.selectedItems[0].trackingId && 
        [this.PRE_COMPLETE, this.COMPLETE].includes(code.action)) {

      if (code.action === this.PRE_COMPLETE) this.onPreComplete.emit(this.selectedItems);
      else if (code.action === this.COMPLETE) this.onComplete.emit(this.selectedItems);

      this.canUndoRequestToCancel = false;
    } else {
      let message = `Are you sure you want to tag the selected ${this.selectedItems.length > 1 ? 'books' : 'book'} as ${code.label}?`;
      
      if (code.action === this.REQUEST_CANCEL.action) {
        message = `Are you sure you want to submit a Request to Cancel for the selected ${this.selectedItems.length > 1 ? 'books' : 'book'}?`
      } else if (code.action === this.UNDO_REQUEST_CANCEL.action) {
        message = `Are you sure you want to undo the Request to Cancel for the selected ${this.selectedItems.length > 1 ? 'books' : 'book'}?`
      }
      
      if (this.selectedItems && this.selectedItems.length > 0) {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, { autoFocus: false });
        dialogRef.componentInstance.confirmMessage = message;
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            switch (code.action) {
              case this.REQUEST_CANCEL.action: {
                this.requestToCancel.emit(selectedItemsCodes);
                this.selectedItems = [];
                this.canRequestToCancel = false;
                break;
              }
              case this.UNDO_REQUEST_CANCEL.action: {
                this.undoRequestToCancel.emit(selectedItemsCodes);
                this.selectedItems = [];
                this.canUndoRequestToCancel = false;
                break;
              }
              case this.PRE_COMPLETE.action: {
                this.onPreComplete.emit(this.selectedItems);
                this.selectedItems = [];
                this.canUndoRequestToCancel = false;
                break;
              }
              case this.COMPLETE.action: {
                this.onComplete.emit(this.selectedItems);
                this.selectedItems = [];
                this.canUndoRequestToCancel = false;
                break;
              }
              case this.CANCEL.action: {
                this.onCancel.emit(this.selectedItems);
                this.selectedItems = [];
                this.canUndoRequestToCancel = false;
                break;
              }
            }
          }
        });
      }
    }
  }

  adjustmentConfirmDialog(forRefund: boolean) {
    const dialogRef = this.dialog.open(OrderItemAdjustmentDialogComponent, { width: "50%" });
    dialogRef.componentInstance.item = this.selectedItems[0];
    dialogRef.componentInstance.forRefund = forRefund;
    dialogRef.componentInstance.close.subscribe(() => {
      dialogRef.close();
    });
    dialogRef.componentInstance.refund.subscribe((result) => {
      if (result) {
        this.onAdjust.emit({
          orderCode: this.orderDetailsValue.code,
          itemId: this.selectedItems[0].id,
          amount: result.amount,
          type: result.type,
          forRefund: forRefund,
          remarks: result.remarks,
          itemCodeName: `${this.selectedItems[0].referenceCode} ${this.selectedItems[0].referenceName}`,
        });
        this.canRequestToCancel = false;
        dialogRef.close();
      }
    });
  }

  getItemLatestStatusHistory(orderItemId) {
		this.loaderMessagingService.showPageLoader(true);
    this.crudService
      .getById<OrderItemStatusHistory>(getItemLatestStatusHistoryEndpoint.concat(`?orderItemId=${orderItemId}`))
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        this.selectedItemStatusHistory = response;
        this.getItemAdjustmentHistory(orderItemId);
      }, this.errorHandlerService.handleError);
  }

  getItemCreatedStatusDetails(item) {
    this.crudService
      .getById<OrderItemStatusHistory>(getItemCreatedStatusHistoryEndpoint.concat(`?orderItemId=${item.id}`))
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        if(response){
          item.createdStatusHistoryModifiedDate = response.dateModified ? response.dateModified : "";
          item.createdStatusHistoryModifiedBy = response.modifiedBy ? response.modifiedBy : "";
          item.createdStatusHistoryStatus = response.status ? response.status : "";
        }
      }, this.errorHandlerService.handleError);
  }

  getItemAdjustmentHistory(orderItemId) {
    this.crudService
      .getById<OrderItemAdjustmentHistory>(getItemAdjustmentHistoryEndpoint.concat(`?orderItemId=${orderItemId}`))
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        this.selectedItemAdjustmentHistory = response;
      }, this.errorHandlerService.handleError, this.handleCompletion);
  }

	handleCompletion = (): void => {
		this.loaderMessagingService.showPageLoader(false);
  };
}
